import { useCallback } from "react";
import { supabase } from "../../../supabaseClient";
import { useAuth } from "../../../context/AuthContext";
import { requestTournamentIndexNow } from "../../../utility/requestIndexNow";

export const usePayoutManagement = (
  tournamentId,
  userPermissions,
  refetch,
  refetchVerification
) => {
  const { user } = useAuth();

  const handleVerifyPayouts = useCallback(async () => {
    try {
      // First, check if a verification state already exists for results
      const { data: existingState, error: fetchError } = await supabase
        .from("verificationStates")
        .select("id")
        .eq("tournamentId", tournamentId)
        .eq("entityType", "results")
        .is("entityId", null) // Use .is() instead of .eq() for null values
        .maybeSingle();

      if (fetchError && fetchError.code !== "PGRST116") {
        // PGRST116 = no rows returned
        throw fetchError;
      }

      // Get the verifiedBy value from the actual user ID
      const verifiedBy = user?.id || null;

      if (existingState) {
        // Update existing verification state
        const { error } = await supabase
          .from("verificationStates")
          .update({
            isVerified: true,
            verifiedAt: new Date().toISOString(),
            verifiedBy: verifiedBy,
          })
          .eq("id", existingState.id);

        if (error) throw error;
      } else {
        // Create new verification state - explicitly set entityId to null
        const insertData = {
          tournamentId,
          entityType: "results",
          isVerified: true,
          verifiedAt: new Date().toISOString(),
          verifiedBy: verifiedBy,
        };

        // Only add entityId if it's explicitly null to avoid string conversion
        if (insertData.entityId === undefined) {
          insertData.entityId = null;
        }

        const { error } = await supabase
          .from("verificationStates")
          .insert(insertData);

        if (error) throw error;
      }

      // Refetch so Lock Results writes the public snapshot before leaving preview.
      if (refetch) await refetch();
      if (refetchVerification) await refetchVerification();
      void requestTournamentIndexNow(tournamentId);
      return true;
    } catch (error) {
      console.error("Error finalizing results:", error);
      return false;
    }
  }, [tournamentId, user?.id, refetch, refetchVerification]);

  const handleUnlockPayouts = useCallback(async () => {
    try {
      // First, check if a verification state already exists for results
      const { data: existingState, error: fetchError } = await supabase
        .from("verificationStates")
        .select("id")
        .eq("tournamentId", tournamentId)
        .eq("entityType", "results")
        .is("entityId", null) // Use .is() instead of .eq() for null values
        .maybeSingle();

      if (fetchError && fetchError.code !== "PGRST116") {
        // PGRST116 = no rows returned
        throw fetchError;
      }

      if (existingState) {
        // Update existing verification state
        const { error } = await supabase
          .from("verificationStates")
          .update({
            isVerified: false,
            verifiedAt: null,
            verifiedBy: null,
          })
          .eq("id", existingState.id);

        if (error) throw error;
      } else {
        // Create new verification state - explicitly set entityId to null
        const insertData = {
          tournamentId,
          entityType: "results",
          isVerified: false,
          verifiedAt: null,
          verifiedBy: null,
        };

        // Only add entityId if it's explicitly null to avoid string conversion
        if (insertData.entityId === undefined) {
          insertData.entityId = null;
        }

        const { error } = await supabase
          .from("verificationStates")
          .insert(insertData);

        if (error) throw error;
      }

      // Refetch data to update the UI
      if (refetch) await refetch();
      if (refetchVerification) await refetchVerification();
      void requestTournamentIndexNow(tournamentId);
    } catch (error) {
      console.error("Error unlocking results:", error);
    }
  }, [tournamentId, refetch, refetchVerification]);

  return {
    handleVerifyPayouts,
    handleUnlockPayouts,
  };
};
